import { Grid } from 'antd';
import { isNil } from 'lodash/fp';
import { memo, useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import { type BannerDataFragment } from '../../../../api/fragments/BannerData';
import PortalLoadingElement from '../../../../components/PortalLoadingElement';
import breakpoints from '../../../../utilities/breakpoints';
import { allowedExtensions } from '../../../../utilities/extensions';
import useTranslatedString from '../../../../utilities/useTranslatedString';
import { BannerContainer, BannerText, ExternalLinkContent, StyledImage } from '../ModelConfiguratorDetailsPage/Banner';
import AffixWrapper from '../ModelConfiguratorDetailsPage/components/AffixWrapper';

const Video = styled.video`
    max-width: 100vw;
    object-fit: cover;

    @media (min-width: ${breakpoints.md}) {
        aspect-ratio: 16/9;
    }

    @media (max-width: ${breakpoints.sm}) {
        aspect-ratio: 1/1;
    }
`;

type HeaderImageProps = {
    banner: BannerDataFragment;
    externalUrl?: string;
    loading?: boolean;
};

const HeaderImage = ({ banner, externalUrl, loading }: HeaderImageProps) => {
    const screens = Grid.useBreakpoint();
    const [showExternalLink, setShowExternalLink] = useState<boolean>(!!externalUrl);

    const bannerExtension = useMemo(() => {
        if (screens.lg) {
            if (banner.bannerImage?.filename) {
                return banner.bannerImage.filename.split('.').pop();
            }

            return null;
        }

        if (banner.mobileBannerImage?.filename) {
            return banner.mobileBannerImage.filename.split('.').pop();
        }

        if (banner.bannerImage?.filename) {
            return banner.bannerImage?.filename.split('.').pop();
        }

        return null;
    }, [banner.bannerImage?.filename, banner.mobileBannerImage?.filename, screens.lg]);

    const bannerAsset: string = useMemo(() => {
        if (screens.lg) {
            return banner.bannerImage?.url ?? null;
        }

        return banner.mobileBannerImage?.url ?? banner.bannerImage?.url ?? null;
    }, [banner.bannerImage?.url, banner.mobileBannerImage?.url, screens.lg]);

    const translate = useTranslatedString();

    const content = useMemo(() => {
        if (loading) {
            return <PortalLoadingElement />;
        }

        return (
            <>
                {allowedExtensions.video.includes(`.${bannerExtension}`) && (
                    <Video autoPlay loop muted playsInline>
                        <source src={bannerAsset} type={`video/${bannerExtension}`} />
                    </Video>
                )}
                {!isNil(banner.bannerImage) && allowedExtensions.image.includes(`.${bannerExtension}`) && (
                    <StyledImage preview={false} src={bannerAsset} />
                )}
                {banner && banner.isActive && (
                    <BannerText $textAlign={banner.bannerTextPosition}>{translate(banner.bannerText)}</BannerText>
                )}
            </>
        );
    }, [loading, bannerExtension, bannerAsset, banner, translate]);

    const onExternalLinkClose = useCallback(() => setShowExternalLink(false), [setShowExternalLink]);

    return (
        <>
            {showExternalLink && (
                <AffixWrapper>
                    <ExternalLinkContent onClose={onExternalLinkClose} url={externalUrl} />
                </AffixWrapper>
            )}
            <BannerContainer>{content}</BannerContainer>
        </>
    );
};

export default memo(HeaderImage);
